import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { cn } from "@/lib/utils";
import { Check, Download, Home, ShoppingBag, Palette, Clock, ChevronRight, Sparkles, Star, Zap, Image, FolderOpen, ArrowDown, Users, TrendingUp } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Analytics Tracking
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/Mobile|Android|iPhone|iPad/.test(ua)) return 'Mobile';
  if (/Tablet|iPad/.test(ua)) return 'Tablet';
  return 'Desktop';
};

const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null
  };
};

const trackVisit = async () => {
  try {
    const utm = getUTMParams();
    await fetch(`${API_URL}/api/track/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        page: window.location.pathname,
        referrer: document.referrer || null,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        device: getDeviceType(),
        browser: navigator.userAgent.split(' ').pop()
      })
    });
  } catch (e) {
    console.log('Track visit failed:', e);
  }
};

const trackClick = async (buttonId, buttonText, destinationUrl) => {
  try {
    await fetch(`${API_URL}/api/track/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        button_id: buttonId,
        button_text: buttonText,
        destination_url: destinationUrl
      })
    });
  } catch (e) {
    console.log('Track click failed:', e);
  }
};

// Gumroad URL
const GUMROAD_URL = "https://digitalprintsclub.gumroad.com/l/gqkol";

// Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c57b062b-4748-46fe-bcbf-ffe65cfb07ea/artifacts/d7rm6vzz_DPC.jpg";

// Video URLs
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_instant-prints-8/artifacts/qj3oli9a_openart-68278a5acb0ea660c79878adc19fe285-efa879ac-a6de-4dd3-96af-26c2c106f6c5_1775686274536_4d728902.mp4";
const TRANSFORMATION_VIDEO = "https://customer-assets.emergentagent.com/job_94e1e7ad-d54f-42f0-af07-563132f1bcff/artifacts/d3ebhoad_openart-9a07dd04975022b2995e384cd5157097-5d53617f-8d56-4d38-8d3e-802b03d36be1_1775647586467_a05518aa.mp4";

const WATCH_IT_WORK_VIDEO = "https://customer-assets.emergentagent.com/job_94e1e7ad-d54f-42f0-af07-563132f1bcff/artifacts/3brltgtj_openart-f9f7bc5cf989ff067b11c2a329efaa2d-61db4ed7-3b02-43ee-a80b-9c6cce7463aa_1775755940715_72e96e76.mp4";
const IMAGES = {
  nursery: "https://customer-assets.emergentagent.com/job_instant-prints-8/artifacts/mclj3ekh_NURSERY_163.jpg",
  animal: "https://customer-assets.emergentagent.com/job_instant-prints-8/artifacts/fsil35ap_ANIMALS_COLLECTION_732.png",
  kitchen: "https://customer-assets.emergentagent.com/job_instant-prints-8/artifacts/8gta6zms_KITCHEN_POSTERS_527.jpg",
  flowers: "https://customer-assets.emergentagent.com/job_instant-prints-8/artifacts/76oe1zvr_FLOWERS_510.jpg",
  coastal: "https://customer-assets.emergentagent.com/job_instant-prints-8/artifacts/v7ebt3vv_COASTAL_PHOTOS_766.jpg",
  boho: "https://customer-assets.emergentagent.com/job_instant-prints-8/artifacts/3o73h2q2_BOHO_2084.jpg",
};

// Scroll-triggered animation hook
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.15, ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

// Animated section wrapper
const FadeInSection = ({ children, className = "", delay = 0 }) => {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-700 ease-out",
        isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Countdown Timer
const CountdownTimer = ({ variant = "dark" }) => {
  const getEndDate = () => {
    const stored = localStorage.getItem('countdown_end_date');
    if (stored) return new Date(stored);
    const defaultEnd = new Date();
    defaultEnd.setDate(defaultEnd.getDate() + 3);
    localStorage.setItem('countdown_end_date', defaultEnd.toISOString());
    return defaultEnd;
  };

  const calculateTimeLeft = useCallback(() => {
    const difference = getEndDate() - new Date();
    if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }, []);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const isDark = variant === "dark";

  return (
    <div className="flex justify-center gap-3" data-testid="countdown-timer">
      {[
        { value: timeLeft.days, label: 'Days' },
        { value: timeLeft.hours, label: 'Hrs' },
        { value: timeLeft.minutes, label: 'Min' },
        { value: timeLeft.seconds, label: 'Sec' }
      ].map((item, i) => (
        <div key={i} className="text-center">
          <div className={cn(
            "rounded-lg px-3 py-2 min-w-[50px]",
            isDark ? "bg-white/10 text-white" : "bg-stone-900 text-white"
          )}>
            <span className="text-xl md:text-2xl font-bold tabular-nums">
              {String(item.value).padStart(2, '0')}
            </span>
          </div>
          <span className={cn(
            "text-[10px] uppercase mt-1 block",
            isDark ? "text-white/50" : "text-stone-500"
          )}>{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// CTA Button with proper variant support
const CTAButton = ({ id, children, variant = "dark", size = "default", className = "" }) => {
  const handleClick = () => trackClick(id, children, GUMROAD_URL);

  return (
    <a
      href={GUMROAD_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all shadow-xl hover:scale-[1.02] cta-primary",
        size === "large" ? "px-8 py-5 text-lg md:text-xl" : "px-6 py-4 text-base md:text-lg",
        variant === "light"
          ? "bg-white text-stone-900 hover:bg-stone-100"
          : "bg-stone-900 text-white hover:bg-stone-800",
        className
      )}
      data-testid={id}
    >
      {children}
      <ChevronRight className="w-5 h-5" />
    </a>
  );
};

// Benefit Icon Component
const BenefitIcon = ({ icon: Icon, text }) => (
  <div className="flex flex-col items-center text-center gap-2">
    <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center">
      <Icon className="w-5 h-5 text-stone-700" />
    </div>
    <span className="text-sm text-stone-600 font-medium">{text}</span>
  </div>
);

// Testimonial Card
const TestimonialCard = ({ quote, name, delay = 0 }) => (
  <FadeInSection delay={delay}>
    <div className="bg-white p-5 rounded-2xl shadow-sm border border-stone-100 testimonial-card">
      <div className="flex gap-1 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
        ))}
      </div>
      <p className="text-stone-700 text-sm leading-relaxed mb-3">"{quote}"</p>
      <p className="text-stone-500 text-sm font-medium">-- {name}</p>
    </div>
  </FadeInSection>
);

// Style Preview Card
const StyleCard = ({ image, title, delay = 0 }) => (
  <FadeInSection delay={delay}>
    <div className="relative rounded-xl overflow-hidden aspect-square group cursor-pointer">
      <img src={image} alt={title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      <span className="absolute bottom-3 left-3 text-white text-sm font-medium drop-shadow-lg">{title}</span>
    </div>
  </FadeInSection>
);

// Social proof notification
const SocialProofToast = () => {
  const [visible, setVisible] = useState(false);
  const [currentProof, setCurrentProof] = useState(0);
  const proofs = [
    { name: "Sarah from London", action: "just purchased", time: "2 min ago" },
    { name: "James from Manchester", action: "just purchased", time: "5 min ago" },
    { name: "Emma from Bristol", action: "just purchased", time: "8 min ago" },
    { name: "Lily from Edinburgh", action: "just downloaded", time: "12 min ago" },
  ];

  useEffect(() => {
    const showTimeout = setTimeout(() => {
      setVisible(true);
      const interval = setInterval(() => {
        setVisible(false);
        setTimeout(() => {
          setCurrentProof(prev => (prev + 1) % proofs.length);
          setVisible(true);
        }, 500);
      }, 6000);
      return () => clearInterval(interval);
    }, 4000);
    return () => clearTimeout(showTimeout);
  }, []);

  const proof = proofs[currentProof];

  return (
    <div
      className={cn(
        "fixed bottom-20 md:bottom-6 left-4 z-40 bg-white rounded-xl shadow-2xl border border-stone-200 p-3 pr-5 flex items-center gap-3 transition-all duration-500 max-w-[320px]",
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8 pointer-events-none"
      )}
      data-testid="social-proof-toast"
    >
      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
        <Check className="w-5 h-5 text-green-600" />
      </div>
      <div>
        <p className="text-stone-900 text-sm font-medium">{proof.name}</p>
        <p className="text-stone-500 text-xs">{proof.action} &middot; {proof.time}</p>
      </div>
    </div>
  );
};

// Main App
function App() {
  useEffect(() => {
    trackVisit();
  }, []);

  const handleCtaClick = (buttonId, buttonText) => {
    trackClick(buttonId, buttonText, GUMROAD_URL);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="landing-page">
      
      {/* Social Proof Toast */}
      <SocialProofToast />

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[90vh] md:min-h-screen flex flex-col" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            poster={IMAGES.coastal}
            className="w-full h-full object-cover"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        </div>

        {/* Logo */}
        <div className="relative z-10 pt-4 px-4">
          <img src={LOGO_URL} alt="Digital Prints Club" className="h-12 md:h-14" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center items-center text-center px-4 py-8">
          <div className="max-w-3xl animate-fade-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm mb-6 animate-pulse-subtle">
              <Sparkles className="w-4 h-4" />
              100,000+ Printable Designs
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-4">
              Turn Any Room Into<br />
              <span className="italic font-normal">Pinterest-Worthy</span> Decor<br />
              in Minutes
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl mx-auto">
              Download 100,000+ wall prints instantly — no design skills needed
            </p>

            {/* CTA */}
            <CTAButton id="hero-cta" size="large" className="mb-4">
              Get Instant Access — £19
            </CTAButton>

            {/* Urgency */}
            <p className="text-white/60 text-sm flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              Limited-time offer — price increasing soon
            </p>

            {/* Trust Signals */}
            <div className="flex flex-wrap justify-center gap-4 mt-6 text-white/50 text-xs">
              <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Instant Download</span>
              <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Secure Checkout</span>
              <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Lifetime Access</span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="relative z-10 pb-8 text-center">
          <div className="animate-bounce">
            <ArrowDown className="w-6 h-6 text-white/50 mx-auto" />
          </div>
        </div>
      </section>

      {/* ==================== TRANSFORMATION SECTION ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="transformation-section">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-10">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-400 mb-2">Before & After</p>
              <h2 className="text-3xl md:text-4xl font-light text-stone-900">
                See the Transformation
              </h2>
            </div>
          </FadeInSection>

          {/* Video Showcase */}
          <FadeInSection delay={100}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-8 bg-stone-200">
              <video
                autoPlay
                loop
                muted
                playsInline
                poster={IMAGES.nursery}
                className="w-full"
              >
                <source src={TRANSFORMATION_VIDEO} type="video/mp4" />
              </video>
            </div>
          </FadeInSection>

          {/* Style Grid */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <StyleCard image={IMAGES.nursery} title="Nursery" delay={0} />
            <StyleCard image={IMAGES.kitchen} title="Kitchen" delay={50} />
            <StyleCard image={IMAGES.boho} title="Boho" delay={100} />
            <StyleCard image={IMAGES.coastal} title="Coastal" delay={150} />
            <StyleCard image={IMAGES.flowers} title="Botanical" delay={200} />
            <StyleCard image={IMAGES.animal} title="Kids" delay={250} />
          </div>

          <FadeInSection delay={300}>
            <div className="text-center mt-8">
              <CTAButton id="transformation-cta">
                Start Your Transformation
              </CTAButton>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== QUICK BENEFITS STRIP ==================== */}
      <section className="py-10 px-4 bg-stone-100" data-testid="benefits-strip">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <BenefitIcon icon={Image} text="100,000+ Prints" />
              <BenefitIcon icon={Zap} text="Instant Download" />
              <BenefitIcon icon={Home} text="Print Anywhere" />
              <BenefitIcon icon={ShoppingBag} text="Home or Selling" />
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-12">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-400 mb-2">Simple as 1-2-3</p>
              <h2 className="text-3xl md:text-4xl font-light text-stone-900">
                How It Works
              </h2>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Purchase & Download", desc: "Click the button, pay once (£19), and get instant access to all 100,000+ files via email." },
              { step: "2", title: "Pick Your Prints", desc: "Browse organised folders — nursery, kitchen, boho, coastal, abstract & more. Choose your favourites." },
              { step: "3", title: "Print & Display", desc: "Print at home or at any shop (Boots, Staples, Costco). Frame them and transform your space." },
            ].map((item, i) => (
              <FadeInSection key={i} delay={i * 120}>
                <div className="text-center">
                  <div className="w-14 h-14 bg-stone-900 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>

          <FadeInSection delay={400}>
            <div className="text-center mt-10">
              <CTAButton id="how-it-works-cta">
                Get Started Now
              </CTAButton>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== WHAT YOU GET SECTION ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="what-you-get-section">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-10">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-400 mb-2">Everything Included</p>
              <h2 className="text-3xl md:text-4xl font-light text-stone-900 mb-4">
                What You Get
              </h2>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {[
              { icon: Image, text: "100,000+ high-resolution wall prints" },
              { icon: Palette, text: "Multiple styles: boho, kitchen, kids, office, abstract, coastal" },
              { icon: Sparkles, text: "Lifetime access to all designs" },
              { icon: FolderOpen, text: "Organised folders by category" },
              { icon: Download, text: "Instant download — JPG & PNG formats" },
              { icon: Zap, text: "Print at home or any print shop" },
            ].map((item, i) => (
              <FadeInSection key={i} delay={i * 80}>
                <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                  <div className="w-10 h-10 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-stone-600" />
                  </div>
                  <span className="text-stone-700">{item.text}</span>
                </div>
              </FadeInSection>
            ))}
          </div>

          <FadeInSection delay={500}>
            <div className="text-center">
              <p className="text-stone-500 mb-6 text-lg">
                No waiting. No shipping. <span className="font-semibold text-stone-700">Instant access after purchase.</span>
              </p>
              <CTAButton id="what-you-get-cta">
                Get All 100,000+ Prints
              </CTAButton>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== VALUE STACK SECTION ==================== */}
      <section className="py-16 px-4 bg-stone-900 text-white" data-testid="value-stack-section">
        <div className="max-w-3xl mx-auto text-center">
          <FadeInSection>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-400 mb-2">Real Value</p>
            <h2 className="text-3xl md:text-4xl font-light mb-10">
              What This Would Normally Cost
            </h2>
          </FadeInSection>

          <FadeInSection delay={100}>
            <div className="space-y-4 mb-10">
              <div className="flex justify-between items-center py-3 border-b border-stone-700">
                <span className="text-stone-300">Etsy prints (£5-£15 each)</span>
                <span className="text-stone-400 line-through">£500+</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-stone-700">
                <span className="text-stone-300">Interior design bundles</span>
                <span className="text-stone-400 line-through">£200+</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-stone-700">
                <span className="text-stone-300">Commercial license</span>
                <span className="text-stone-400 line-through">£100+</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-white font-semibold text-lg">Total Value</span>
                <span className="text-stone-400 line-through text-xl">£800+</span>
              </div>
            </div>
          </FadeInSection>

          <FadeInSection delay={200}>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
              <p className="text-stone-400 mb-2">Your price today</p>
              <p className="text-5xl md:text-6xl font-bold text-white mb-2">£19</p>
              <p className="text-stone-400 text-sm mb-4">One-time payment &middot; Lifetime access</p>
              <div className="flex items-center justify-center gap-2 text-green-400 text-sm font-medium">
                <TrendingUp className="w-4 h-4" />
                You save £781+
              </div>
            </div>
          </FadeInSection>

          <FadeInSection delay={300}>
            <CTAButton id="value-stack-cta" variant="light" size="large">
              Unlock Everything for £19
            </CTAButton>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== USE CASE SPLIT ==================== */}
      <section className="py-16 px-4 bg-stone-50" data-testid="use-case-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Home Decor */}
            <FadeInSection>
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100 h-full">
                <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center mb-6">
                  <Home className="w-7 h-7 text-stone-700" />
                </div>
                <h3 className="text-2xl font-medium text-stone-900 mb-4">
                  Decorate Your Home Instantly
                </h3>
                <ul className="space-y-3 mb-6">
                  {[
                    "Transform any room in minutes",
                    "Gallery walls made easy",
                    "Match any aesthetic or style",
                    "Print any size you need",
                    "Save hundreds on wall art"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-stone-600">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-3 gap-2">
                  <img src={IMAGES.nursery} alt="Nursery decor" loading="lazy" className="rounded-lg aspect-square object-cover" />
                  <img src={IMAGES.kitchen} alt="Kitchen decor" loading="lazy" className="rounded-lg aspect-square object-cover" />
                  <img src={IMAGES.boho} alt="Boho decor" loading="lazy" className="rounded-lg aspect-square object-cover" />
                </div>
              </div>
            </FadeInSection>

            {/* Side Hustle */}
            <FadeInSection delay={150}>
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100 h-full">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-6">
                  <ShoppingBag className="w-7 h-7 text-amber-700" />
                </div>
                <h3 className="text-2xl font-medium text-stone-900 mb-4">
                  Start a Print Side Hustle
                </h3>
                <ul className="space-y-3 mb-6">
                  {[
                    "Sell prints on Etsy & Amazon",
                    "Create passive income",
                    "No inventory needed",
                    "Commercial license included",
                    "Ready-to-sell designs"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-stone-600">
                      <Check className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <p className="text-amber-800 font-medium">
                    100,000+ designs = unlimited earning potential
                  </p>
                </div>
              </div>
            </FadeInSection>
          </div>

          <FadeInSection delay={300}>
            <div className="text-center mt-10">
              <CTAButton id="use-case-cta" size="large">
                Get Started for £19
              </CTAButton>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== SOCIAL PROOF ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="social-proof-section">
        <div className="max-w-5xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-10">
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-base text-stone-600 font-medium">Rated 4.9/5 by 1,000+ customers</p>
              <p className="text-stone-400 text-sm mt-1">Join home decorators & Etsy sellers worldwide</p>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-4">
            <TestimonialCard
              quote="I decorated my entire nursery for under £10. These prints are incredible quality!"
              name="Sarah M."
              delay={0}
            />
            <TestimonialCard
              quote="Started selling on Etsy last month. Already made back my investment 10x!"
              name="James K."
              delay={100}
            />
            <TestimonialCard
              quote="My gallery wall looks like it cost hundreds. Best £19 I've ever spent."
              name="Emma T."
              delay={200}
            />
          </div>

          <FadeInSection delay={300}>
            <div className="flex flex-wrap justify-center gap-6 mt-10 text-stone-400 text-sm">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>1,000+ happy customers</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span>4.9 star average rating</span>
              </div>
              <div className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                <span>50,000+ downloads</span>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== VIDEO SECTION ==================== */}
      <section className="py-16 px-4 bg-stone-100" data-testid="video-section">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-8">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-400 mb-2">Watch It Work</p>
              <h2 className="text-3xl md:text-4xl font-light text-stone-900">
                From Blank Wall to Beautiful
              </h2>
            </div>
          </FadeInSection>

          <FadeInSection delay={100}>
            <div className="relative rounded-2xl overflow-hidden shadow-xl bg-stone-200">
              <video
                autoPlay
                loop
                muted
                playsInline
                poster={IMAGES.kitchen}
                className="w-full"
              >
                <source src={WATCH_IT_WORK_VIDEO} type="video/mp4" />
              </video>
            </div>
          </FadeInSection>

          <FadeInSection delay={200}>
            <div className="text-center mt-8">
              <CTAButton id="video-cta">
                Create Your Transformation
              </CTAButton>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== URGENCY SECTION ==================== */}
      <section className="py-16 px-4 bg-stone-900 text-white" data-testid="urgency-section">
        <div className="max-w-3xl mx-auto text-center">
          <FadeInSection>
            <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm mb-6 animate-pulse-subtle">
              <Clock className="w-4 h-4" />
              Limited Time Offer
            </div>

            <h2 className="text-3xl md:text-4xl font-light mb-4">
              Price Increasing Soon
            </h2>
            <p className="text-stone-400 mb-8">
              Get 100,000+ prints for just £19 before the price goes up
            </p>
          </FadeInSection>

          <FadeInSection delay={100}>
            <div className="mb-8">
              <CountdownTimer variant="dark" />
            </div>
          </FadeInSection>

          <FadeInSection delay={200}>
            <CTAButton id="urgency-cta" variant="light" size="large">
              Download Now — £19
            </CTAButton>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== FAQ SECTION ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-light text-stone-900">
                Quick Questions
              </h2>
            </div>
          </FadeInSection>

          <FadeInSection delay={100}>
            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: "How do I get my prints?", a: "Instantly! After purchase, you'll receive a download link via email. Access all 100,000+ prints immediately." },
                { q: "Where can I print them?", a: "Anywhere! Print at home, at Staples, Costco, Boots, or any online print service. All files are high-resolution and print-ready." },
                { q: "What sizes are included?", a: "Multiple sizes: 5x7, 8x10, 11x14, 16x20, 18x24, A4, and A3. Perfect for any frame or space." },
                { q: "Can I sell these prints?", a: "Yes! Commercial license is included. Sell on Etsy, Amazon, or anywhere you like." },
                { q: "Is this a subscription?", a: "No! One-time payment of £19. Lifetime access. No recurring fees ever." },
              ].map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-stone-50 rounded-xl px-5 border-none">
                  <AccordionTrigger className="text-left font-medium hover:no-underline py-4">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 pb-4">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== FINAL CTA SECTION ==================== */}
      <section className="py-20 px-4 bg-stone-900 text-white" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <FadeInSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-4">
              Ready to Transform Your Space?
            </h2>
            <p className="text-lg text-stone-400 mb-8">
              100,000+ prints. Instant access. Just £19.
            </p>

            <CTAButton id="final-cta" variant="light" size="large" className="mb-6">
              Unlock 100,000 Prints
            </CTAButton>

            <div className="flex flex-wrap justify-center gap-4 text-stone-400 text-sm mt-4">
              <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Instant Download</span>
              <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Lifetime Access</span>
              <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Commercial License</span>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="py-8 px-4 bg-stone-950 text-stone-500 text-center text-sm">
        <img src={LOGO_URL} alt="Digital Prints Club" className="h-8 mx-auto mb-4 opacity-50" />
        <p>&copy; {new Date().getFullYear()} Digital Prints Club. All rights reserved.</p>
      </footer>

      {/* ==================== STICKY MOBILE CTA ==================== */}
      <div className="fixed bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur-lg border-t border-stone-200 md:hidden z-50 safe-area-bottom sticky-cta-shadow" data-testid="sticky-cta">
        <a
          href={GUMROAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleCtaClick('sticky-cta', 'Get Instant Access')}
          className="flex items-center justify-center gap-2 bg-stone-900 text-white w-full py-4 rounded-full text-lg font-semibold shadow-xl cta-primary"
          data-testid="sticky-cta-button"
        >
          Get Instant Access — £19
        </a>
        <p className="text-center text-stone-400 text-xs mt-1">One-time payment &middot; No subscription</p>
      </div>

    </div>
  );
}

export default App;
